import { ReactElement, useMemo } from 'react'
import {
    SVGs,
    Drawer,
    Grid,
    Image,
    Modal,
    StarsWithTotal,
    SVG,
    TabsGroup,
    Text,
} from '../..'
import useElementSize from '../../Hooks/useElementSize'
import { ProductModalProps } from './props'
import { Footer, Header, StyledProductModal } from './styles'
import BookTab from './Tabs/Book'
import CompetencesTab from './Tabs/Competences'
import ProfileTab from './Tabs/Profile'
import ReviewsTab from './Tabs/Reviews'

const ProductModal = ({
    activeTab = 'book',
    bookTab,
    competencesTab,
    extraTabs = [],
    footerButtons,
    noReviewsText = 'New at RaskRask',
    numberOfReviews = 0,
    onChangeTab = () => undefined,
    onClose,
    onOpen,
    open,
    productImage,
    productName,
    profileTab,
    rating = 0,
    reviewsTab,
    ...props
}: ProductModalProps): ReactElement => {
    const [footerRef, footerSize] = useElementSize()

    const header = useMemo(
        () => (
            <Header>
                <Image src={productImage} alt={`Image of ${productName}`} />
                <Grid>
                    <Text size="l" bold primary>
                        {productName}
                    </Text>
                    {numberOfReviews > 0 ? (
                        <StarsWithTotal
                            numberOfReviews={numberOfReviews}
                            rating={rating}
                            size="m"
                        />
                    ) : (
                        <Text>{noReviewsText}</Text>
                    )}
                </Grid>
                <SVG src={SVGs.Close} onClick={onClose} />
            </Header>
        ),
        [
            noReviewsText,
            numberOfReviews,
            onClose,
            productImage,
            productName,
            rating,
        ]
    )

    const tabs = useMemo(
        () =>
            [
                {
                    value: 'book',
                    label: bookTab?.label ?? 'Book',
                    content: <BookTab {...bookTab} />,
                    hide: bookTab ? bookTab?.hide : true,
                },
                {
                    value: 'profile',
                    label: profileTab?.label ?? 'Profile',
                    content: <ProfileTab {...profileTab} />,
                    hide: profileTab ? profileTab?.hide : true,
                },
                {
                    value: 'reviews',
                    label: reviewsTab?.label ?? 'Reviews',
                    content: (
                        <ReviewsTab
                            {...reviewsTab}
                            numberOfReviews={numberOfReviews}
                            rating={rating}
                        />
                    ),
                    hide: reviewsTab ? reviewsTab?.hide : true,
                },
                ...extraTabs,
                {
                    value: 'competences',
                    label: competencesTab?.label ?? 'Competences',
                    content: <CompetencesTab {...competencesTab} />,
                    hide: competencesTab ? competencesTab?.hide : true,
                },
            ].filter((tab) => ('hide' in tab ? !tab.hide : true)),
        [
            bookTab,
            competencesTab,
            extraTabs,
            numberOfReviews,
            profileTab,
            rating,
            reviewsTab,
        ]
    )

    const tabsGroup = useMemo(
        () => (
            <TabsGroup
                activeTab={activeTab}
                onChange={onChangeTab}
                tabs={tabs}
                css={{
                    gap: '$3',
                }}
            />
        ),
        [onChangeTab, tabs, activeTab]
    )

    const footer = useMemo(
        () => footerButtons && <Footer ref={footerRef}>{footerButtons}</Footer>,
        [footerButtons, footerRef]
    )

    const allContent = useMemo(
        () => (
            <>
                {header}
                {tabsGroup}
                {footer}
            </>
        ),
        [footer, header, tabsGroup]
    )

    const typeOfComponent = useMemo(
        () =>
            props.type === 'modal' ? (
                <Modal
                    open={open}
                    onClose={onClose}
                    onOpen={onOpen}
                    css={{
                        w: 'calc(100% - 32px)',
                    }}
                >
                    {allContent}
                </Modal>
            ) : (
                <Drawer
                    open={open}
                    onClose={onClose}
                    onOpen={onOpen}
                    css={{ '@mobile': { pb: footerSize.height + 16 } }}
                >
                    {allContent}
                </Drawer>
            ),
        [allContent, footerSize.height, onClose, onOpen, open, props.type]
    )

    return <StyledProductModal>{typeOfComponent}</StyledProductModal>
}

export default ProductModal

export { StyledProductModal }
